import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'
import { Button, IconButton, InputAdornment, TextField, Tooltip, ClickAwayListener, MenuItem } from '@mui/material'

import { config } from '../config'
import { useSocket } from '../hooks/use-socket'
import PlusIcon from '../assets/icons/plus-icon.svg'
import CopyIcon from '../assets/icons/copy-icon.svg'
import { CryptoTransaction, FormError } from '../types'
import Pagination from '../components/common/pagination'
import SearchIcon from '../assets/icons/search-icon.svg'
import {getCryptoWalletData, resolveDocumentsAccessToken } from '../api/documents.api'
import { copyTextToClipboard, numberWithCommas } from '../utils'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { baseButtonTextWhiteBg, textFieldWhiteBg } from '../theme/custom-styles'
import CryptoTransactionListItem from '../components/crypto-management/crypto-transaction-list-item'
import { TransferCryptoFundsModal } from '../components/crypto-management/transfer-crypto-funds-modal'
import { ConfirmTransferCryptoFundsModal } from '../components/crypto-management/confirm-transfer-crypto-funds-modal'

const CryptoManagement = () => {
  const navigate = useNavigate()
  const dataLoaded = useRef<boolean>(false)
  const currentListPageRef = useRef<number>(1)
  const { userSession } = useContext(UserContext)
  const transactionToSearchRef = useRef<string>('')
  const { socket, isSocketInitialized, connectExcelReaderSocket } = useSocket()
  const { t } = useTranslation(['crypto-management'])
  const { setScrollToTop } = useContext(AppStateContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const transactionsDataRef = useRef<Array<CryptoTransaction>>([])

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [walletAddress, setWalletAddress] = useState('')
  const [feeToPayInETH, setFeeToPayInETH] = useState<number>(0)
  const [feeToPayInTRX, setFeeToPayInTRX] = useState<number>(0)
  const [feeToPayInUSD, setFeeToPayInUSD] = useState<number>(0)
  const [selectedNetwork, setSelectedNetwork] = useState('tron')
  const [walletUSDTBalance, setWalletUSDTBalance] = useState('0')
  const [walletUSDCBalance, setWalletUSDCBalance] = useState('0')
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [tooltipOpened, setTooltipOpened] = useState<boolean>(false)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [transactionToSearch, setTransactionToSearch] = useState<string>('')
  const [walletNativeBalance, setWalletNativeBalance] = useState<string>('0')
  const [walletAddressToSendTo, setWalletAddressToSendTo] = useState<string>('')
  const [amountToTransferValue, setAmountToTransferValue] = useState<string>('')
  const [transactionsData, setTransactionsData] = useState<Array<CryptoTransaction>>([])
  const [transactionsListData, setTransactionsListData] = useState<Array<CryptoTransaction>>([])
  const [openTransferCryptoFundsModal, setOpenTransferCryptoFundsModal] = useState<boolean>(false)
  const [searchedTransactionsData, setSearchedTransactionsData] = useState<Array<CryptoTransaction>>([])
  const [openConfirmTransferCryptoFundsModal, setOpenConfirmTransferCryptoFundsModal] = useState<boolean>(false)

  useEffect(() => {
    if (!userSession) {
      return
    }

    if ((userSession && !userSession?.subRole) || (userSession && userSession?.subRole && userSession?.subRole !== 'crypto-specialist')) {
      navigate('/404')
      return
    }

    handleGetCryptoWalletData(selectedNetwork)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    connectExcelReaderSocket()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!socket) return

    socket.on('cryptoWalletBalanceChange', ({ newBalance, address }) => {
      if ((selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') && String(walletAddress).toLowerCase() === String(address).toLowerCase()) {
        setWalletNativeBalance(String(Number(newBalance) / 1e6))
      }
    })

    socket.on('cryptoETHWalletBalanceChange', ({ newBalance }) => {
      if (selectedNetwork === 'eth') {
        setWalletNativeBalance(String(Number(newBalance) / 1e18))
      }
    })

    socket.on('cryptoWalletUSDTBalanceChange', ({ newBalance, address }) => {
      if (String(walletAddress).toLowerCase() === String(address).toLowerCase()) {
        setWalletUSDTBalance(String(Number(newBalance) / 1e6))
      }
    })

    socket.on('cryptoWalletUSDCBalanceChange', ({ newBalance }) => {
      setWalletUSDCBalance(String(Number(newBalance) / 1e6))
    })

    socket.on('cryptoWalletNewTransactions', ({ data }) => {
      if (Array.isArray(data)) {
        if ((selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') && String(walletAddress).toLowerCase() === String(data[0].walletUsed).toLowerCase()) {
          handleUpdateTransactionsData(data)
        }

        return
      }

      if ((selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') && String(walletAddress).toLowerCase() === String(data.walletUsed).toLowerCase()) {
        handleUpdateTransactionsData(data)
      }
    })

    socket.on('cryptoETHWalletNewTransactions', ({ data }) => {
      if (selectedNetwork === 'eth') {
        handleUpdateTransactionsData(data)
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isSocketInitialized, selectedNetwork])

  useEffect(() => {
    if (dataLoaded.current === true) {
      dataLoaded.current = false
      setLoadingLocalData(true)
    }

    handleGetCryptoWalletData(selectedNetwork)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNetwork])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = transactionToSearchRef.current !== '' ? searchedTransactionsData.slice(firstPageIndex, lastPageIndex) : transactionsData.slice(firstPageIndex, lastPageIndex)

    setTransactionsListData(paymentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, transactionsData])

  const handleUpdateTransactionsData = (transactionsDataToUpdate: Array<CryptoTransaction>) => {
    const transactionsUpdatedData = [...transactionsDataRef.current]

    for (let i = 0; i < transactionsDataToUpdate.length; i++) {
      const currentTransactionIndex = transactionsUpdatedData.findIndex((transactionData) => String(transactionData.transactionId) === transactionsDataToUpdate[i].transactionId)

      if (currentTransactionIndex === -1) {
        transactionsUpdatedData.push(transactionsDataToUpdate[i])
      } else {
        transactionsUpdatedData[currentTransactionIndex].status = transactionsDataToUpdate[i].status
        transactionsUpdatedData[currentTransactionIndex].confirmations = transactionsDataToUpdate[i].confirmations
        transactionsUpdatedData[currentTransactionIndex].tokenTransferred = transactionsDataToUpdate[i].tokenTransferred
      }
    }

    transactionsUpdatedData.sort((a: { blockTimestamp: number }, b: { blockTimestamp: number }) => b.blockTimestamp - a.blockTimestamp)
    transactionsDataRef.current = transactionsUpdatedData
    setTransactionsData(transactionsUpdatedData)
  }

  const handleGetCryptoWalletData = async (selectedNetwork: string) => {
    try {
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { USDTBalance, USDCBalance, nativeBalance, publicKey, transactions }
      } = await getCryptoWalletData(accessToken, selectedNetwork)

      setWalletAddress(publicKey)
      setTransactionsData(transactions)

      if (selectedNetwork === 'tron') {
        setWalletUSDTBalance(USDTBalance)
        setWalletNativeBalance(String(Number(nativeBalance) / 1e6))
      }

      if (selectedNetwork === 'tron-paxum') {
        setWalletUSDTBalance(USDTBalance)
        setWalletNativeBalance(String(Number(nativeBalance) / 1e6))
      }

      if (selectedNetwork === 'tron-wpagos') {
        setWalletUSDTBalance(USDTBalance)
        setWalletNativeBalance(String(Number(nativeBalance) / 1e6))
      }

      if (selectedNetwork === 'eth') {
        setWalletUSDCBalance(USDCBalance)
        const nativeBalanceToSet = Number(nativeBalance) === 0 ? 0 : Number(nativeBalance) / 1e18 >= 1 ? (Number(nativeBalance) / 1e18).toFixed(2) : (Number(nativeBalance) / 1e18).toFixed(6)
        setWalletNativeBalance(String(nativeBalanceToSet))
      }

      transactionsDataRef.current = transactions

      if (dataLoaded.current === false) {
        dataLoaded.current = true
        setLoadingLocalData(false)
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }

      return
    }
  }

  const handleSetTransactionToSearch = (transactionToSearch: string, showError: boolean) => {
    currentListPageRef.current = 1
    transactionToSearchRef.current = transactionToSearch

    setErrors({})
    setCurrentListPage(1)
    setTransactionToSearch(transactionToSearch)

    if (transactionToSearch === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentTransactionsData = transactionsDataRef.current ?? []
      const paymentsPerPage = currentTransactionsData.slice(firstPageIndex, lastPageIndex)

      setTransactionsListData(paymentsPerPage)
      setSearchedTransactionsData([])

      if (showError) setErrors({ transactionToSearch: t('actionsBar.searchField.errors.emptyField') })
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      <div
        style={{ flexDirection: 'column' }}
        className="section-container"
      >
        <div
          style={{ marginTop: '0px' }}
          className="customer-company-fundings-history-container"
        >
          <TextField
            select
            fullWidth
            variant="standard"
            sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            inputProps={{
              style: { textAlign: 'center' }
            }}
            label="Red elegida"
            error={!!errors.bankAccountType}
            helperText={errors.bankAccountType}
            value={selectedNetwork}
            onChange={(event) => setSelectedNetwork(event.target.value)}
          >
            <MenuItem
              disabled
              value=""
            >
              {t('textFields.networkSelect.select')}
            </MenuItem>
            <MenuItem value="tron">{t('textFields.networkSelect.tron')}</MenuItem>
            <MenuItem value="tron-paxum">{t('textFields.networkSelect.tronPaxum')}</MenuItem>
            <MenuItem value="tron-wpagos">{t('textFields.networkSelect.tronWpagos')}</MenuItem>
            <MenuItem value="eth">{t('textFields.networkSelect.eth')}</MenuItem>
          </TextField>
        </div>
        <div
          className="client-section-container"
          style={{ padding: '0px', marginTop: '20px' }}
        >
          <div className="client-section-left-container">
            <div
              style={{ flex: '1' }}
              className="bank-balances-management-content bank-balances-management-client-content"
            >
              <div
                className="bank-balances-container"
                style={{ minHeight: 'unset', flex: '1' }}
              >
                <div
                  style={{ flex: '1' }}
                  className="bank-balance-square-container"
                >
                  <div className="bank-balance-square-info-container">
                    {dataLoaded.current ? (
                      <div
                        style={{ fontSize: '22px' }}
                        className="bank-balance-square-main-data"
                      >
                        {selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos'
                          ? `${Number(walletUSDTBalance) === 0 ? 0 : Number(walletUSDTBalance) >= 1 ? numberWithCommas(Number(walletUSDTBalance).toFixed(2)) : numberWithCommas(Number(walletUSDTBalance).toFixed(6))} USDT`
                          : `${
                              Number(walletUSDCBalance) === 0 ? 0 : Number(walletUSDCBalance) >= 1 ? numberWithCommas(Number(walletUSDCBalance).toFixed(2)) : numberWithCommas(Number(walletUSDCBalance).toFixed(6))
                            } USDC`}
                      </div>
                    ) : (
                      <div
                        className="data-container-loader"
                        style={{ padding: '12px', width: '200px', alignSelf: 'center' }}
                      ></div>
                    )}
                    <div className="bank-balance-square-main-data">
                      <div style={{ fontSize: '20px', color: '#004A5C' }}>{t('topData.currentUSDTBalance')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="client-section-left-container">
            <div className="bank-balances-management-content bank-balances-management-client-content">
              <div
                className="bank-balances-container"
                style={{ minHeight: 'unset' }}
              >
                <div className="bank-balance-square-container">
                  <div className="bank-balance-square-info-container">
                    <ClickAwayListener onClickAway={() => setTooltipOpened(false)}>
                      <Tooltip
                        onClose={() => setTooltipOpened(false)}
                        open={tooltipOpened}
                        placement="top"
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={t('components.tooltip.copiedToClipboard')}
                        slotProps={{
                          popper: {
                            disablePortal: true
                          }
                        }}
                      >
                        <div
                          className="bank-balance-square-main-data"
                          onClick={() => {
                            copyTextToClipboard(walletAddress)
                            setTooltipOpened(true)
                          }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer' }}
                        >
                          {dataLoaded.current ? (
                            <div style={{ fontSize: '22px', color: '#737373', fontWeight: 'normal', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{walletAddress}</div>
                          ) : (
                            <div
                              className="data-container-loader"
                              style={{ padding: '12px', width: '200px', alignSelf: 'center' }}
                            ></div>
                          )}
                          <img
                            src={CopyIcon}
                            alt="copy-icon"
                          />
                        </div>
                      </Tooltip>
                    </ClickAwayListener>
                    <div className="bank-balance-square-main-data">
                      <Button
                        fullWidth
                        variant="text"
                        sx={baseButtonTextWhiteBg}
                        startIcon={
                          <img
                            src={PlusIcon}
                            alt="plus-icon"
                          />
                        }
                        className="users-management-actions-bar-button-action"
                        onClick={() => setOpenTransferCryptoFundsModal(true)}
                      >
                        <div className="users-management-actions-bar-button-action-text">{t('topData.buttons.send')}</div>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{ marginTop: '20px' }}
          className="customer-company-fundings-history-container"
        >
          <div className="actions-bar">
            <div className="left-content">
              <div className="title">
                {transactionsData.length === 0
                  ? t('actionsBar.thereIsNoTransactionsToShow')
                  : transactionsData.length > 0 && t('actionsBar.transactions', { transactionsCount: transactionToSearchRef.current !== '' ? searchedTransactionsData.length : transactionsData.length })}
              </div>
            </div>
            <TextField
              variant="standard"
              sx={textFieldWhiteBg}
              value={transactionToSearch}
              error={!!errors.transactionToSearch}
              helperText={errors.transactionToSearch}
              label={t('actionsBar.searchField.placeholder')}
              className="search-input"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleSetTransactionToSearch(transactionToSearch, true)
                }
              }}
              onChange={(event) => handleSetTransactionToSearch(event.target.value, true)}
              onBlur={() => {
                if (transactionToSearch === '') setErrors({})
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                    >
                      <img
                        src={SearchIcon}
                        alt="search-icon"
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>
          {!loadingLocalData && transactionsData.length > 0 && (
            <div className="table-header-container">
              <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('table.headers.date').toUpperCase()}</div>
              <div className="customer-company-table-column customer-company-table-width-45 table-header-text">{t('table.headers.txHash').toUpperCase()}</div>
              <div className="customer-company-table-column customer-company-table-width-15 table-header-text customer-company-display-none-lg">{t('table.headers.amount').toUpperCase()}</div>
              <div className="customer-company-table-column customer-company-table-width-20 table-header-text customer-company-display-none-sm">{t('table.headers.typeAndRefWallet').toUpperCase()}</div>
              <div className="customer-company-table-column customer-company-table-width-10 table-header-text">{t('table.headers.status').toUpperCase()}</div>
            </div>
          )}
          {loadingLocalData ? (
            <div className="users-management-users-list-container">
              <div className="users-management-user-data-container-loader"></div>
              <div className="users-management-user-data-container-loader"></div>
              <div className="users-management-user-data-container-loader"></div>
              <div className="users-management-user-data-container-loader"></div>
              <div className="users-management-user-data-container-loader"></div>
            </div>
          ) : (
            <>
              <div className="documents-list-container">
                {transactionsListData.map((transactionData: CryptoTransaction, index: number) => {
                  return (
                    <CryptoTransactionListItem
                      key={index}
                      transactionData={transactionData}
                      handleRedirectToTransactionDetails={(selectedTransactionData: CryptoTransaction) => {
                        if (selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') {
                          window.open(`${config.tronNetwork.blockExplorerUrl}/#/transaction/${selectedTransactionData.transactionId}`, '_blank')
                          return
                        }

                        window.open(`${config.ethereumNetwork.blockExplorerUrl}/tx/${selectedTransactionData.transactionId}`, '_blank')
                      }}
                    />
                  )
                })}
              </div>
              <Pagination
                pageSize={pageSize}
                currentPage={currentListPage}
                handlePaginationPageChange={(page: number) => {
                  setTimeout(() => {
                    setScrollToTop(true)
                    setCurrentListPage(page)
                    currentListPageRef.current = page
                  }, 100)
                }}
                handleChangePageSize={(pageSizeToSet: string) => {
                  setScrollToTop(true)
                  setPageSize(Number(pageSizeToSet))
                }}
                totalCount={transactionToSearchRef.current !== '' ? searchedTransactionsData.length : transactionsData.length}
              />
            </>
          )}
        </div>
      </div>
      {openTransferCryptoFundsModal && (
        <TransferCryptoFundsModal
          feeToPayInETH={feeToPayInETH}
          setFeeToPayInETH={setFeeToPayInETH}
          feeToPayInUSD={feeToPayInUSD}
          feeToPayInTRX={feeToPayInTRX}
          selectedNetwork={selectedNetwork}
          setFeeToPayInUSD={setFeeToPayInUSD}
          setFeeToPayInTRX={setFeeToPayInTRX}
          walletUSDTBalance={walletUSDTBalance}
          walletUSDCBalance={walletUSDCBalance}
          walletNativeBalance={walletNativeBalance}
          walletAddressToSendTo={walletAddressToSendTo}
          amountToTransferValue={amountToTransferValue}
          setWalletAddressToSendTo={setWalletAddressToSendTo}
          setAmountToTransferValue={setAmountToTransferValue}
          handleOpenConfirmationModal={() => setOpenConfirmTransferCryptoFundsModal(true)}
          handleOpenStateOfModal={(openState: boolean) => setOpenTransferCryptoFundsModal(openState)}
        />
      )}
      {openConfirmTransferCryptoFundsModal && (
        <ConfirmTransferCryptoFundsModal
          walletToUse={walletAddress}
          feeToPayInUSD={feeToPayInUSD}
          feeToPayInTRX={feeToPayInTRX}
          feeToPayInETH={feeToPayInETH}
          selectedNetwork={selectedNetwork}
          walletAddressToSendTo={walletAddressToSendTo}
          amountToTransferValue={amountToTransferValue}
          handleOpenStateOfModal={(openState: boolean, cryptoPaymentSent?: boolean) => {
            if (cryptoPaymentSent) {
              setWalletAddressToSendTo('')
              setAmountToTransferValue('')
              setOpenTransferCryptoFundsModal(false)
            }

            setOpenConfirmTransferCryptoFundsModal(openState)
          }}
        />
      )}
    </>
  )
}

export default CryptoManagement
